/**
 * TransferSankey - Sankey diagram for transfers between accounts
 *
 * Replaces the list-based TransferSection with a proper Sankey visualization
 * showing source accounts on the left, target accounts on the right,
 * and flows (edges) connecting them.
 *
 * WP-FLOW-REFACTOR-2 Phase 3: Transfer Sankey Frontend
 */

import { memo, useMemo, useState, useCallback } from 'react';
import { layoutTransferSankey } from './TransferSankeyLayout';
import type {
  TransferSankeyData,
  TransferSankeyNode,
  TransferSankeyEdge,
  PositionedTransferNode,
  PositionedTransferEdge,
} from './TransferSankeyTypes';
import { COLORS } from './constants';
import { FlowTooltip, type TooltipContent } from './FlowTooltip';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';

// ============================================================================
// Types
// ============================================================================

export interface TransferSankeyProps {
  /** Transfer Sankey data from the API */
  data: TransferSankeyData;
  /** Period start for context */
  periodStart?: string;
  /** Period end for context */
  periodEnd?: string;
  /** Callback when a node is clicked */
  onNodeClick?: (node: TransferSankeyNode) => void;
  /** Callback when an edge is clicked */
  onEdgeClick?: (edge: TransferSankeyEdge) => void;
  /** Additional CSS class */
  className?: string;
}

// ============================================================================
// Layout Constants
// ============================================================================

const CANVAS = {
  WIDTH: 600,
  MIN_HEIGHT: 200,
  NODE_WIDTH: 120,
  COLUMN_GAP: 200,
  NODE_MIN_HEIGHT: 30,
  NODE_MAX_HEIGHT: 100,
  NODE_PADDING: 12,
};

// ============================================================================
// Main Component
// ============================================================================

function TransferSankeyComponent({
  data,
  // periodStart and periodEnd are available for future use (context display)
  periodStart: _periodStart,
  periodEnd: _periodEnd,
  onNodeClick,
  onEdgeClick,
  className,
}: TransferSankeyProps) {
  // Suppress unused variable warnings - these are kept for API consistency
  void _periodStart;
  void _periodEnd;
  // Tooltip state
  const [tooltip, setTooltip] = useState<TooltipContent | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [highlightedEdgeId, setHighlightedEdgeId] = useState<string | null>(null);

  // Calculate layout
  const layout = useMemo(
    () =>
      layoutTransferSankey(data.sourceNodes, data.targetNodes, data.edges, {
        width: CANVAS.WIDTH,
        height: Math.max(
          CANVAS.MIN_HEIGHT,
          Math.max(data.sourceNodes.length, data.targetNodes.length) * 60
        ),
        nodeWidth: CANVAS.NODE_WIDTH,
        columnGap: CANVAS.COLUMN_GAP,
        nodeMinHeight: CANVAS.NODE_MIN_HEIGHT,
        nodeMaxHeight: CANVAS.NODE_MAX_HEIGHT,
        nodePadding: CANVAS.NODE_PADDING,
      }),
    [data]
  );

  // Event handlers
  const handleNodeHover = useCallback(
    (node: TransferSankeyNode | null, event?: React.MouseEvent) => {
      if (!node || !event) {
        setTooltip(null);
        return;
      }

      setTooltipPosition({ x: event.clientX, y: event.clientY });
      setTooltip({
        type: 'node',
        nodeType: node.isExternal ? 'account' : 'account',
        title: node.name,
        value: node.totalAmount,
      });
    },
    []
  );

  const handleEdgeHover = useCallback(
    (edge: TransferSankeyEdge | null, event?: React.MouseEvent) => {
      if (!edge || !event) {
        setTooltip(null);
        setHighlightedEdgeId(null);
        return;
      }

      setHighlightedEdgeId(edge.id);
      setTooltipPosition({ x: event.clientX, y: event.clientY });

      const sourceNode = data.sourceNodes.find((n) => n.id === edge.sourceNodeId);
      const targetNode = data.targetNodes.find((n) => n.id === edge.targetNodeId);

      setTooltip({
        type: 'edge',
        source: sourceNode?.name || 'Unknown',
        target: targetNode?.name || 'Unknown',
        value: edge.amount,
        transactionCount: edge.transactionCount,
      });
    },
    [data.sourceNodes, data.targetNodes]
  );

  const handleNodeClick = useCallback(
    (node: TransferSankeyNode) => {
      onNodeClick?.(node);
    },
    [onNodeClick]
  );

  const handleEdgeClick = useCallback(
    (edge: TransferSankeyEdge) => {
      onEdgeClick?.(edge);
    },
    [onEdgeClick]
  );

  // Empty state
  if (data.edges.length === 0) {
    return null;
  }

  // Calculate SVG height with padding
  const svgHeight = Math.max(layout.totalHeight + 20, CANVAS.MIN_HEIGHT);

  return (
    <div className={cn('transfer-sankey', className)}>
      {/* Section header */}
      <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
        <span
          className="w-3 h-3 rounded-full"
          style={{ backgroundColor: COLORS.transfer.border }}
          aria-hidden="true"
        />
        Transfers Between Accounts
      </h3>

      {/* Sankey diagram */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-x-auto">
        <svg
          width={CANVAS.WIDTH}
          height={svgHeight}
          viewBox={`0 0 ${CANVAS.WIDTH} ${svgHeight}`}
          role="img"
          aria-label={`${data.totalCount} transfers totaling ${formatCurrency(data.totalAmount)}`}
          className="transfer-sankey-svg"
        >
          {/* Edges (rendered first, below nodes) */}
          <g className="transfer-edges">
            {layout.edges.map((posEdge) => (
              <TransferEdgePath
                key={posEdge.edge.id}
                edge={posEdge}
                isHighlighted={highlightedEdgeId === posEdge.edge.id}
                onClick={() => handleEdgeClick(posEdge.edge)}
                onHover={(hovering, event) =>
                  handleEdgeHover(hovering ? posEdge.edge : null, event)
                }
              />
            ))}
          </g>

          {/* Source nodes (left column) */}
          <g className="source-nodes">
            {layout.sourceColumn.map((pn) => (
              <TransferNodeRect
                key={pn.node.id}
                positioned={pn}
                onClick={() => handleNodeClick(pn.node)}
                onHover={(event) => handleNodeHover(pn.node, event)}
                onLeave={() => handleNodeHover(null)}
              />
            ))}
          </g>

          {/* Target nodes (right column) */}
          <g className="target-nodes">
            {layout.targetColumn.map((pn) => (
              <TransferNodeRect
                key={pn.node.id}
                positioned={pn}
                onClick={() => handleNodeClick(pn.node)}
                onHover={(event) => handleNodeHover(pn.node, event)}
                onLeave={() => handleNodeHover(null)}
              />
            ))}
          </g>
        </svg>
      </div>

      {/* Summary */}
      <div className="mt-2 text-xs text-gray-500">
        {data.totalCount} transfer{data.totalCount !== 1 ? 's' : ''} totaling{' '}
        {formatCurrency(data.totalAmount)}
      </div>

      {/* Tooltip */}
      <FlowTooltip content={tooltip} position={tooltipPosition} />
    </div>
  );
}

// ============================================================================
// Node Subcomponent
// ============================================================================

interface TransferNodeRectProps {
  positioned: PositionedTransferNode;
  onClick: () => void;
  onHover: (event: React.MouseEvent) => void;
  onLeave: () => void;
}

function TransferNodeRect({
  positioned,
  onClick,
  onHover,
  onLeave,
}: TransferNodeRectProps) {
  const { node, x, y, width, height } = positioned;

  // Truncate name if too long
  const displayName =
    node.name.length > 14 ? `${node.name.slice(0, 14)}...` : node.name;

  return (
    <g
      className="transfer-node cursor-pointer"
      onClick={onClick}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
    >
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={4}
        fill={node.isExternal ? '#F3F4F6' : 'white'}
        stroke={node.isExternal ? '#9CA3AF' : COLORS.accountTierA}
        strokeWidth={1}
        strokeDasharray={node.isExternal ? '4 2' : 'none'}
        className="transition-all duration-150"
      />
      <text
        x={x + width / 2}
        y={y + height / 2}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={node.isExternal ? '#6B7280' : COLORS.text.primary}
        fontSize={12}
        fontWeight={node.isExternal ? 400 : 500}
        fontStyle={node.isExternal ? 'italic' : 'normal'}
        className="select-none pointer-events-none"
      >
        {displayName}
      </text>
    </g>
  );
}

// ============================================================================
// Edge Subcomponent
// ============================================================================

interface TransferEdgePathProps {
  edge: PositionedTransferEdge;
  isHighlighted: boolean;
  onClick: () => void;
  onHover: (hovering: boolean, event?: React.MouseEvent) => void;
}

function TransferEdgePath({
  edge,
  isHighlighted,
  onClick,
  onHover,
}: TransferEdgePathProps) {
  return (
    <path
      d={edge.path}
      fill="none"
      stroke={isHighlighted ? COLORS.edgeHover.transfer : COLORS.edge.transfer}
      strokeWidth={edge.strokeWidth}
      className="cursor-pointer transition-all duration-150"
      onClick={onClick}
      onMouseEnter={(e) => onHover(true, e)}
      onMouseLeave={(e) => onHover(false, e)}
    />
  );
}

// ============================================================================
// Exports
// ============================================================================

export const TransferSankey = memo(TransferSankeyComponent);
export default TransferSankey;
